import { createContext, useContext, type ReactNode, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Form } from '@/components/ui/form';
import {
  EMPTY_METRIC_DRAFT,
  type MetricDraft,
  metricDraftSchema,
  toMetricDraft,
} from '@/modules/analytics/utils/metricDraft';
import type { EditedMetricRef } from '@/modules/analytics/utils/resolveMetricIndex';
import { createYupResolver } from '@/util/forms/createYupResolver';

export type ReportEditorState = { reportId: string } & (
  { kind: 'add' } | ({ kind: 'edit' } & EditedMetricRef)
);

interface ReportDetailUiContextValue {
  editor: ReportEditorState | null;
  openAddMetric: (reportId: string) => void;
  openEditMetric: (reportId: string, ref: EditedMetricRef) => void;
  closeEditor: () => void;
  scrollRequest: number;
  requestScroll: () => void;
}

const ReportDetailUiContext = createContext<ReportDetailUiContextValue | null>(null);

export const ReportDetailUiProvider = ({ children }: { children: ReactNode }) => {
  const [editor, setEditor] = useState<ReportEditorState | null>(null);
  const [scrollRequest, setScrollRequest] = useState(0);
  const requestScroll = () => setScrollRequest((n) => n + 1);
  const form = useForm<MetricDraft>({
    mode: 'onTouched',
    resolver: createYupResolver<MetricDraft>(metricDraftSchema),
    defaultValues: EMPTY_METRIC_DRAFT,
  });

  const openAddMetric = (reportId: string) => {
    form.reset(EMPTY_METRIC_DRAFT);
    setEditor({ reportId, kind: 'add' });
    requestScroll();
  };

  const openEditMetric = (reportId: string, ref: EditedMetricRef) => {
    form.reset(toMetricDraft(ref.original));
    setEditor({ reportId, kind: 'edit', ...ref });
    requestScroll();
  };

  const closeEditor = () => {
    setEditor(null);
    form.reset(EMPTY_METRIC_DRAFT);
  };

  const value = {
    editor,
    openAddMetric,
    openEditMetric,
    closeEditor,
    scrollRequest,
    requestScroll,
  };

  return (
    <ReportDetailUiContext.Provider value={value}>
      <Form {...form}>{children}</Form>
    </ReportDetailUiContext.Provider>
  );
};

export const useReportDetailUi = (reportId?: string): ReportDetailUiContextValue => {
  const ctx = useContext(ReportDetailUiContext);
  if (!ctx) {
    throw new Error('useReportDetailUi must be used within a ReportDetailUiProvider');
  }
  if (reportId !== undefined && ctx.editor?.reportId !== reportId) {
    return { ...ctx, editor: null };
  }
  return ctx;
};
